import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import Spinner from "react-bootstrap/Spinner";
import Button from "react-bootstrap/Button";
import ProgressBar from "react-bootstrap/ProgressBar";
import { PersonFill, PersonFillExclamation, Search, XCircle } from 'react-bootstrap-icons';
import { useNavigate } from 'react-router-dom';
import UserService from "../../services/user.service";
import Form from "react-bootstrap/Form";
import { Container, Row, Col, InputGroup, Alert } from "react-bootstrap";
import Table from 'react-bootstrap/Table';
import { getSubsite } from '../../reducers/auth';

const CHUNK = 100;

const getTomorrowDate = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return tomorrow.toISOString().split('T')[0];
};

const AdminUsersBulkEdit = () => {
  const navigate = useNavigate();
  const currentSubsite = useSelector(getSubsite);

  const [users, setUsers] = useState([]);
  const [loadStatus, setLoadStatus] = useState('idle'); // idle | loading | succeeded | failed
  const [loadProgress, setLoadProgress] = useState({ loaded: 0, total: 0 });
  const [loadError, setLoadError] = useState(null);

  const [message, setMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentChecks, setCurrentChecks] = useState([]);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [sortedData, setSortedData] = useState([]);
  const [sendEmails, setSendEmails] = useState(true);
  const [scheduleBalance, setScheduleBalance] = useState(false);
  const [activationDate, setActivationDate] = useState(getTomorrowDate());
  const [balanceDrafts, setBalanceDrafts] = useState({});

  const [jobStatus, setJobStatus] = useState(null); // null | job object
  const pollRef = useRef(null);

  const clearPoll = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  useEffect(() => () => clearPoll(), []);

  const loadAllUsers = async (search = searchTerm) => {
    setLoadStatus('loading');
    setLoadError(null);
    setLoadProgress({ loaded: 0, total: 0 });
    try {
      let page = 1;
      let all = [];
      let total = Infinity;
      while (all.length < total) {
        const res = await UserService.getBulkUsersPage(page, CHUNK, search);
        total = res.count;
        all = all.concat(res.data || []);
        setLoadProgress({ loaded: all.length, total });
        if (!res.data || res.data.length === 0) break;
        page += 1;
        // safety: avoid infinite loop if count is wrong
        if (page > 500) break;
      }
      setUsers(all);
      setLoadStatus('succeeded');
    } catch (err) {
      console.error(err);
      setLoadStatus('failed');
      setLoadError(err?.response?.data?.message || err.message || 'Failed to load users');
      setUsers([]);
    }
  };

  useEffect(() => {
    loadAllUsers(searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    if (loadStatus !== 'succeeded') return;
    const newSortedData = [...users].sort((a, b) => {
      if (!sortColumn) return 0;
      let valueA = a[sortColumn];
      let valueB = b[sortColumn];
      if (typeof valueA === 'string') valueA = valueA.toLowerCase();
      if (typeof valueB === 'string') valueB = valueB.toLowerCase();
      if (valueA < valueB) return sortDirection === 'asc' ? -1 : 1;
      if (valueA > valueB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    setSortedData(newSortedData);
    setCurrentChecks(newSortedData.map(u => !!u.subsite_balances?.[0]?.balanceRollsOver));
  }, [loadStatus, users, sortColumn, sortDirection]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearchTerm(searchInput.trim());
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearchTerm("");
  };

  const checkChanged = (idx, userId) => {
    UserService.updateBalanceRollsOver({ userId: userId, balanceRollsOver: !currentChecks[idx] });
    const checks = [...currentChecks];
    checks[idx] = !currentChecks[idx];
    setCurrentChecks(checks);
  };

  const pollJob = (jobId) => {
    clearPoll();
    pollRef.current = setInterval(async () => {
      try {
        const job = await UserService.getBulkUpdateJobStatus(jobId);
        setJobStatus(job);
        if (job.status === 'completed' || job.status === 'failed') {
          clearPoll();
          if (job.status === 'completed') {
            setSuccessMessage(job.message);
            if (job.errors?.length) {
              setMessage(job.errors.map((err, i) => (
                <li key={i}>User - {err.id} - {err.error}</li>
              )));
            } else {
              setMessage(null);
            }
            setBalanceDrafts({});
            loadAllUsers(searchTerm);
          } else {
            setMessage([<li key="fail">{job.message || 'Bulk update failed'}</li>]);
          }
        }
      } catch (err) {
        clearPoll();
        setJobStatus(prev => prev ? { ...prev, status: 'failed', message: 'Lost connection to job status' } : null);
        setMessage([<li key="poll">Could not read job status. Refresh and check balances.</li>]);
      }
    }, 1000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);
    setSuccessMessage(null);

    const updates = [];
    for (const user of users) {
      const newBalance = balanceDrafts[user.id];
      let update = null;
      if (newBalance !== undefined && newBalance !== null && String(newBalance).trim() !== "") {
        update = { id: user.id, newBalance: String(newBalance).trim() };
      }
      if (update) updates.push(update);
    }

    if (updates.length === 0) {
      setMessage([<li key="none">No balance changes to save. Fill in New Balance for at least one user.</li>]);
      return;
    }

    if (scheduleBalance && !activationDate) {
      setMessage([<li key="date">Please choose an activation date.</li>]);
      return;
    }

    const scheduleDate = scheduleBalance ? activationDate : null;
    try {
      const start = await UserService.updateUserBalances(updates, sendEmails, scheduleDate);
      setJobStatus({
        id: start.jobId,
        status: start.status || 'pending',
        total: start.total || updates.length,
        processed: 0,
        message: scheduleBalance
          ? `Scheduling ${updates.length} update(s)...`
          : `Updating ${updates.length} user(s)...`,
        scheduled: !!start.scheduled
      });
      pollJob(start.jobId);
    } catch (error) {
      console.error('Error starting bulk update:', error);
      const msg = error?.response?.data?.message || error.message || 'Error starting bulk update.';
      setMessage([<li key="err">{msg}</li>]);
      setJobStatus(null);
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const isItemLimit = currentSubsite?.paymentOptions === "itemlimit";
  const jobRunning = jobStatus && (jobStatus.status === 'pending' || jobStatus.status === 'running');
  const jobPercent = jobStatus?.total
    ? Math.min(100, Math.round((100 * (jobStatus.processed || 0)) / jobStatus.total))
    : 0;

  return (
    <Container style={{ padding: 4 }}>
      <Row>
        <Col>
          <h2>Users Bulk Edit</h2>
        </Col>
      </Row>
      <Row style={{ padding: 4 }}>
        <Col>
          Fill in New Balance for any users you want to change. Blank fields are ignored.
          Updates run in the background so you can watch progress below. You can also schedule
          balance changes for a future date (applied at midnight), same as the individual user page.
        </Col>
      </Row>

      <Row className="mb-3 align-items-end g-2">
        <Col md={6}>
          <Form onSubmit={handleSearchSubmit}>
            <Form.Label>Filter list (optional)</Form.Label>
            <InputGroup>
              <Form.Control
                type="text"
                placeholder="Name, username, or email"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                disabled={jobRunning}
              />
              <Button variant="primary" type="submit" disabled={jobRunning}><Search /> Search</Button>
              {searchTerm && (
                <Button variant="outline-secondary" onClick={handleClearSearch} disabled={jobRunning}>
                  <XCircle /> Clear
                </Button>
              )}
            </InputGroup>
          </Form>
        </Col>
        <Col md={6} className="text-muted">
          {loadStatus === 'loading'
            ? `Loading users… ${loadProgress.loaded}${loadProgress.total ? ` / ${loadProgress.total}` : ''}`
            : `${users.length} user(s) loaded`}
        </Col>
      </Row>

      {loadStatus === 'loading' && (
        <ProgressBar
          className="mb-3"
          now={loadProgress.total ? (100 * loadProgress.loaded) / loadProgress.total : 0}
          label={loadProgress.total ? `${loadProgress.loaded} / ${loadProgress.total}` : undefined}
        />
      )}

      {jobStatus && (
        <Alert variant={jobStatus.status === 'failed' ? 'danger' : jobStatus.status === 'completed' ? 'success' : 'info'} className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <strong>Bulk update status</strong>
            <span className="text-uppercase small">{jobStatus.status}</span>
          </div>
          <div className="mb-2">{jobStatus.message}</div>
          {(jobStatus.status === 'pending' || jobStatus.status === 'running') && (
            <ProgressBar animated now={jobPercent} label={`${jobStatus.processed || 0} / ${jobStatus.total || 0}`} />
          )}
          {jobStatus.status === 'completed' && (
            <div className="small mt-1">
              {jobStatus.scheduled
                ? `Scheduled: ${jobStatus.scheduledCount || 0}`
                : `Updated: ${jobStatus.updatedCount || 0}`}
              {jobStatus.errors?.length ? ` · Errors: ${jobStatus.errors.length}` : ''}
            </div>
          )}
        </Alert>
      )}

      {successMessage && (
        <Alert variant="success" dismissible onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}
      {message && (
        <Alert variant="danger" dismissible onClose={() => setMessage(null)}>
          Errors:
          <ul className="mb-0">{message}</ul>
        </Alert>
      )}
      {loadStatus === 'failed' && (
        <Alert variant="danger">
          {loadError || 'Could not load users.'}{' '}
          <Button size="sm" variant="outline-danger" onClick={() => loadAllUsers(searchTerm)}>Retry</Button>
        </Alert>
      )}

      <Form onSubmit={handleSubmit}>
        <Container style={{ fontSize: 12 }} className="px-0">
          <Table>
            <thead className="thead-dark">
              <tr style={{ backgroundColor: "#333333", color: "#fff" }}>
                <th>Status</th>
                <th onClick={() => handleSort("username")} style={{ cursor: 'pointer' }}>
                  Username {sortColumn === "username" && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
                </th>
                <th onClick={() => handleSort("lastname")} style={{ cursor: 'pointer' }}>
                  Name {sortColumn === "lastname" && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
                </th>
                <th onClick={() => handleSort("title")} style={{ cursor: 'pointer' }}>
                  Title {sortColumn === "title" && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
                </th>
                {isItemLimit ? (
                  <>
                    <th>Item Limit</th>
                    <th>New Item Limit</th>
                  </>
                ) : (
                  <>
                    <th>Balance</th>
                    <th>New Balance</th>
                    <th>Roll Balance</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {loadStatus === 'succeeded' && sortedData.map((user, idx) => (
                <tr key={user.id} style={{ backgroundColor: idx % 2 === 0 ? "#FFFFFF" : "#CCCCCC" }}>
                  <td>
                    <span
                      onClick={() => navigate("/admin/users/" + user.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      {user.registration_id || !user.active
                        ? <PersonFillExclamation size={40} color="#990000" />
                        : <PersonFill size={40} color="#009900" />}
                    </span>
                  </td>
                  <td>{user.username}</td>
                  <td>{user.lastname}, {user.firstname}</td>
                  <td>{user.title || ''}</td>
                  <td>{user.subsite_balances?.[0]?.balance ?? 0}</td>
                  <td>
                    <input
                      id={`user_${user.id}`}
                      type="text"
                      style={{ width: 70 }}
                      disabled={jobRunning}
                      value={balanceDrafts[user.id] ?? ''}
                      onChange={(e) => setBalanceDrafts(prev => ({ ...prev, [user.id]: e.target.value }))}
                    />
                  </td>
                  {!isItemLimit && (
                    <td>
                      <input
                        id={`user_${user.id}_roll`}
                        type="checkbox"
                        style={{ width: 50 }}
                        disabled={jobRunning}
                        checked={!!currentChecks[idx]}
                        onChange={() => checkChanged(idx, user.id)}
                      />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
          {loadStatus === 'succeeded' && sortedData.length === 0 && (
            <div className="text-muted mb-3">No users match these filters.</div>
          )}
        </Container>

        <Form.Group className="mb-3">
          <Form.Check
            type="checkbox"
            id="scheduleBalance"
            label="Schedule balance updates for a future date"
            checked={scheduleBalance}
            disabled={jobRunning}
            onChange={(e) => setScheduleBalance(e.target.checked)}
          />
        </Form.Group>

        {scheduleBalance && (
          <Form.Group className="mb-3" style={{ maxWidth: 280 }}>
            <Form.Label>Activation Date</Form.Label>
            <Form.Control
              type="date"
              min={getTomorrowDate()}
              value={activationDate}
              disabled={jobRunning}
              onChange={(e) => setActivationDate(e.target.value)}
            />
            <Form.Text className="text-muted">
              Funds will be set at 12:00 AM on this date for each user with a New Balance filled in.
            </Form.Text>
          </Form.Group>
        )}

        <Form.Group className="mb-3">
          <Form.Check
            type="checkbox"
            id="sendEmails"
            label={scheduleBalance
              ? "Send email notification when the scheduled balance is applied"
              : "Send email notification to users if their balance changed"}
            checked={sendEmails}
            disabled={jobRunning}
            onChange={(e) => setSendEmails(e.target.checked)}
          />
        </Form.Group>

        <Button type="submit" disabled={jobRunning || loadStatus === 'loading'}>
          {jobRunning ? (
            <>
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
              Running in background…
            </>
          ) : (
            scheduleBalance ? 'Schedule Balance Updates' : 'Update All Users'
          )}
        </Button>
      </Form>
    </Container>
  );
};

export default AdminUsersBulkEdit;
